import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles } from '@material-ui/core/styles';

export const SKELETON_VARIANTS = [
  'content',
  'dashboard',
  'table',
  'form',
  'cards',
  'calendar',
  'course-list',
  'course-catalogue',
  'learning-hub',
  'report-overview',
  'report-table',
  'maturity-report',
];

const useStyles = makeStyles((theme) => ({
  skeleton: {
    width: '100%',
    minHeight: 'calc(100vh - 95px)',
    boxSizing: 'border-box',
    color: 'var(--dm-text-strong, #263548)',
  },
  fullPage: {
    minHeight: '100vh',
    padding: theme.spacing(3),
    backgroundColor: 'var(--bodyColor, #f4f7fa)',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) },
  },
  progress: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    margin: theme.spacing(-2, -2, 2),
    [theme.breakpoints.down('xs')]: { margin: theme.spacing(-1, -1, 2) },
  },
  fullPageProgress: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.modal + 1,
  },
  title: { marginBottom: theme.spacing(2.5) },
  panel: {
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    borderRadius: 16,
    border: '1px solid var(--dm-border, #e7ebf0)',
    background: 'var(--contentColor, #fff)',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.055)',
  },
  block: { borderRadius: 16, marginTop: theme.spacing(3) },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  tablePage: { padding: theme.spacing(2.5) },
  tableTitle: { marginBottom: theme.spacing(2) },
  table: { padding: 0, overflow: 'hidden' },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1.15fr 1.65fr 1fr 1.15fr 64px',
    gap: theme.spacing(2),
    alignItems: 'center',
    minHeight: 54,
    padding: theme.spacing(1.5, 2),
    background: 'var(--dm-row-alt, #f8fafc)',
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1.15fr 1.65fr 1fr 1.15fr 64px',
    gap: theme.spacing(2),
    alignItems: 'center',
    minHeight: 62,
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
    '&:last-child': { borderBottom: 0 },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1.2fr 1fr 1.3fr 64px',
      '& > :nth-child(4), & > :nth-child(5), & > :nth-child(6)': { display: 'none' },
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  formPanel: { padding: theme.spacing(3) },
  formFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  field: { minWidth: 0 },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
  },
  card: { minHeight: 210, padding: 0, overflow: 'hidden' },
  cardMedia: { borderRadius: 0 },
  cardBody: { padding: theme.spacing(2) },
  courseTabs: {
    display: 'flex',
    gap: theme.spacing(3),
    minHeight: 44,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 1),
    alignItems: 'center',
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  courseRows: { display: 'grid', gap: theme.spacing(2) },
  courseRow: {
    display: 'grid',
    gridTemplateColumns: '136px minmax(0, 1fr) 188px',
    gap: theme.spacing(2.5),
    alignItems: 'center',
    minHeight: 152,
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1.5),
    },
  },
  courseRowMedia: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    [theme.breakpoints.down('xs')]: { height: 140 },
  },
  catalogueLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) minmax(0, 3fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  categoryPanel: {
    minHeight: 360,
    padding: theme.spacing(1.75, 1.25),
  },
  categoryRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 32px',
    gap: theme.spacing(1),
    alignItems: 'center',
    minHeight: 40,
    padding: theme.spacing(0.5, 1),
  },
  subcategoryRow: {
    minHeight: 34,
    marginLeft: 12,
    paddingLeft: 12,
  },
  catalogueMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  catalogueToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.25),
    alignItems: 'center',
  },
  catalogueCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  catalogueCard: { minHeight: 420, padding: 0, overflow: 'hidden' },
  catalogueImage: { position: 'relative' },
  catalogueCorner: {
    position: 'absolute !important',
    top: 12,
    left: 12,
    borderRadius: '8px !important',
  },
  hubCallout: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2),
    borderRadius: 8,
  },
  catalogueChipRow: {
    display: 'flex',
    gap: 8,
    marginTop: 10,
  },
  catalogueFactRow: {
    display: 'flex',
    gap: 16,
    marginTop: 10,
  },
  catalogueCardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    paddingTop: 12,
    borderTop: '1px solid var(--dm-border, rgba(148, 163, 184, 0.28))',
  },
  pageHeading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    '& $title': { flex: '1 1 auto', minWidth: 0, marginBottom: 0 },
  },
  accessStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    borderRadius: 8,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' },
  },
  accessItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  hubTabs: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.25),
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  paginationRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  reportTabs: {
    display: 'flex',
    gap: theme.spacing(3),
    minHeight: 52,
    padding: theme.spacing(0, 2.5),
    alignItems: 'center',
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  reportPanel: { padding: 0, overflow: 'hidden' },
  reportBody: { padding: theme.spacing(2.5) },
  filterPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid var(--dm-border, #e7ebf0)',
    background: 'var(--dm-row-alt, #f8fafc)',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  compactTableHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(80px, 1fr))',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    background: 'var(--dm-row-alt, #f8fafc)',
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  compactTableRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(80px, 1fr))',
    gap: theme.spacing(1.5),
    minHeight: 56,
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
    '&:last-child': { borderBottom: 0 },
  },
  reportCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  reportCard: {
    minHeight: 146,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  reportCardHeader: {
    display: 'grid',
    gridTemplateColumns: '48px minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  reportCardActions: { marginTop: 'auto', paddingTop: theme.spacing(2) },
  reportKpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  reportKpi: { minHeight: 132 },
  reportCharts: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  reportChart: { minHeight: 320 },
  calendarMain: { minHeight: 540 },
  calendarToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: 1,
    overflow: 'hidden',
    border: '1px solid var(--dm-border, #e7ebf0)',
    borderRadius: 12,
    background: 'var(--dm-border, #e7ebf0)',
  },
  calendarCell: {
    minHeight: 72,
    padding: theme.spacing(1),
    background: 'var(--contentColor, #fff)',
  },
  contentPanel: { minHeight: 260 },
  '@global': {
    '@media (prefers-reduced-motion: reduce)': {
      '.MuiSkeleton-root': { animation: 'none' },
    },
  },
}));

const PageTitle = ({ classes }) => (
  <div className={classes.title}>
    <Skeleton width="18%" />
    <Skeleton width="42%" height={44} />
    <Skeleton width="62%" />
  </div>
);

PageTitle.propTypes = { classes: PropTypes.object.isRequired };

const DashboardSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <Skeleton variant="rect" height={96} className={classes.block} />
    <Grid container spacing={2} style={{ marginTop: 8 }}>
      {Array.from({ length: 8 }).map((_, index) => (
        <Grid item xs={12} sm={6} lg={3} key={index}>
          <div className={classes.panel}>
            <Skeleton width="55%" />
            <Skeleton width="45%" height={54} />
            <Skeleton width="80%" />
          </div>
        </Grid>
      ))}
    </Grid>
    {Array.from({ length: 3 }).map((_, index) => (
      <Skeleton key={index} variant="rect" height={220} className={classes.block} />
    ))}
  </>
);

DashboardSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const TableSkeleton = ({ classes }) => (
  <div className={`${classes.panel} ${classes.tablePage}`} data-testid="table-skeleton">
    <Skeleton width={180} height={34} className={classes.tableTitle} />
    <div className={classes.toolbar}>
      <Skeleton variant="rect" width="34%" height={42} style={{ borderRadius: 6 }} />
      <Skeleton variant="rect" width={156} height={42} style={{ borderRadius: 6 }} />
    </div>
    <div className={`${classes.panel} ${classes.table}`}>
      <div className={classes.tableHeader}>
        {Array.from({ length: 7 }).map((_, index) => <Skeleton key={index} width="72%" />)}
      </div>
      {Array.from({ length: 10 }).map((_, rowIndex) => (
        <div className={classes.tableRow} key={rowIndex}>
          {Array.from({ length: 7 }).map((_, columnIndex) => (
            <Skeleton key={columnIndex} width={columnIndex === 6 ? 32 : '82%'} height={24} />
          ))}
        </div>
      ))}
    </div>
    <div className={classes.pagination}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} variant="rect" width={30} height={30} style={{ borderRadius: 6 }} />
      ))}
    </div>
  </div>
);

TableSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const FormSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <div className={`${classes.panel} ${classes.formPanel}`}>
      <Skeleton width="28%" height={32} style={{ marginBottom: 24 }} />
      <div className={classes.formFields}>
        {Array.from({ length: 8 }).map((_, index) => (
          <div className={classes.field} key={index}>
            <Skeleton width="34%" />
            <Skeleton variant="rect" height={48} style={{ borderRadius: 6, marginTop: 6 }} />
          </div>
        ))}
      </div>
      <div className={classes.actions}>
        <Skeleton variant="rect" width={104} height={40} style={{ borderRadius: 6 }} />
        <Skeleton variant="rect" width={126} height={40} style={{ borderRadius: 6 }} />
      </div>
    </div>
  </>
);

FormSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const CardsSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <div className={classes.toolbar}>
      <Skeleton variant="rect" width="32%" height={42} style={{ borderRadius: 6 }} />
      <Skeleton variant="rect" width={120} height={42} style={{ borderRadius: 6 }} />
    </div>
    <Grid container spacing={2}>
      {Array.from({ length: 8 }).map((_, index) => (
        <Grid item xs={12} sm={6} lg={3} key={index}>
          <div className={`${classes.panel} ${classes.card}`}>
            <Skeleton variant="rect" height={92} className={classes.cardMedia} />
            <div className={classes.cardBody}>
              <Skeleton width="76%" height={28} />
              <Skeleton width="94%" />
              <Skeleton width="58%" />
            </div>
          </div>
        </Grid>
      ))}
    </Grid>
  </>
);

CardsSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const CourseListSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <div className={classes.courseTabs}>
      <Skeleton width={150} height={28} />
      <Skeleton width={190} height={28} />
    </div>
    <div className={classes.courseRows} data-testid="course-list-skeleton">
      {Array.from({ length: 5 }).map((_, index) => (
        <div className={`${classes.panel} ${classes.courseRow}`} key={index}>
          <Skeleton variant="rect" className={classes.courseRowMedia} />
          <div>
            <Skeleton width="24%" />
            <Skeleton width="62%" height={30} />
            <Skeleton width="92%" />
            <Skeleton width="74%" />
            <Skeleton variant="rect" width="100%" height={8} style={{ borderRadius: 999, marginTop: 14 }} />
          </div>
          <Skeleton variant="rect" width="100%" height={42} style={{ borderRadius: 8 }} />
        </div>
      ))}
    </div>
  </>
);

CourseListSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const pill = { borderRadius: 999 };
const field = { borderRadius: 8 };

const CatalogueCardSkeleton = ({ classes, detailed }) => (
  <div className={`${classes.panel} ${classes.catalogueCard}`}>
    <div className={classes.catalogueImage}>
      <Skeleton variant="rect" height={200} />
      <Skeleton variant="rect" width={128} height={26} className={classes.catalogueCorner} />
    </div>
    <div className={classes.cardBody}>
      <Skeleton width="82%" height={26} />
      {detailed ? (
        <>
          <div className={classes.catalogueChipRow}>
            <Skeleton variant="rect" width={96} height={24} style={{ borderRadius: 8 }} />
            <Skeleton variant="rect" width={118} height={24} style={{ borderRadius: 8 }} />
          </div>
          <div className={classes.catalogueFactRow}>
            <Skeleton width={64} />
            <Skeleton width={72} />
          </div>
        </>
      ) : null}
      <Skeleton width="96%" style={{ marginTop: 10 }} />
      <Skeleton width="88%" />
      <Skeleton width="64%" />
      <div className={classes.catalogueCardActions}>
        {detailed ? <Skeleton variant="rect" width={84} height={36} style={pill} /> : <Skeleton variant="circle" width={20} height={20} />}
        <Skeleton variant="rect" width={detailed ? 92 : 104} height={36} style={pill} />
      </div>
    </div>
  </div>
);

CatalogueCardSkeleton.propTypes = {
  classes: PropTypes.object.isRequired,
  detailed: PropTypes.bool,
};

const CategoryRows = ({ classes, nested = false }) => (
  <>
    <div className={classes.categoryRow}>
      <Skeleton width="46%" />
      <Skeleton variant="rect" width={28} height={18} style={pill} />
    </div>
    {(nested
      ? [
        [true, '72%'],
        [false, '58%'],
        [true, '80%'],
        [false, '64%'],
        [true, '70%'],
        [false, '52%'],
        [true, '76%'],
        [false, '60%'],
      ]
      : [
        [false, '78%'],
        [false, '64%'],
        [false, '84%'],
        [false, '70%'],
        [false, '58%'],
        [false, '80%'],
        [false, '66%'],
        [false, '74%'],
      ]
    ).map(([isChild, width], index) => (
      <div className={`${classes.categoryRow}${isChild ? ` ${classes.subcategoryRow}` : ''}`} key={index}>
        <Skeleton width={width} />
        <Skeleton variant="rect" width={isChild ? 22 : 28} height={isChild ? 16 : 18} style={pill} />
      </div>
    ))}
  </>
);

CategoryRows.propTypes = {
  classes: PropTypes.object.isRequired,
  nested: PropTypes.bool,
};

const CourseCatalogueLayout = ({ classes, showAccess = false }) => (
  <>
    <div className={classes.pageHeading}>
      <PageTitle classes={classes} />
      {showAccess ? <Skeleton variant="rect" width={156} height={36} style={{ ...pill, marginTop: 18 }} /> : null}
    </div>
    {showAccess ? (
      <>
        <div className={`${classes.panel} ${classes.accessStrip}`}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div className={classes.accessItem} key={index}>
              <Skeleton variant="circle" width={22} height={22} />
              <div style={{ flex: 1 }}>
                <Skeleton width="70%" height={14} />
                <Skeleton width="48%" height={18} />
              </div>
            </div>
          ))}
        </div>
        <div className={classes.hubTabs}>
          <Skeleton width={118} height={28} />
          <Skeleton width={146} height={28} />
        </div>
      </>
    ) : null}
    <div className={classes.catalogueLayout} data-testid={showAccess ? 'learning-hub-skeleton' : 'course-catalogue-skeleton'}>
      <div className={`${classes.panel} ${classes.categoryPanel}`}>
        <Skeleton width="62%" height={16} style={{ margin: '2px 8px 14px' }} />
        <CategoryRows classes={classes} nested={showAccess} />
      </div>
      <div className={classes.catalogueMain}>
        <div className={classes.catalogueToolbar}>
          <Skeleton variant="rect" height={40} style={{ ...field, flex: '1 1 220px' }} />
          {showAccess ? (
            <>
              <Skeleton variant="rect" width={108} height={32} style={pill} />
              <Skeleton variant="rect" width={168} height={32} style={pill} />
              <Skeleton variant="rect" width={92} height={32} style={pill} />
              <Skeleton variant="rect" width={124} height={40} style={field} />
              <Skeleton variant="rect" width={124} height={40} style={field} />
              <Skeleton variant="rect" width={168} height={40} style={field} />
            </>
          ) : (
            <Skeleton variant="rect" width={110} height={40} style={field} />
          )}
          {!showAccess ? <Skeleton width={120} height={16} /> : null}
        </div>
        {showAccess ? <Skeleton width={240} height={16} /> : null}
        {showAccess ? (
          <div className={`${classes.panel} ${classes.hubCallout}`}>
            <div style={{ flex: 1 }}>
              <Skeleton width="68%" />
              <Skeleton width="42%" />
            </div>
            <Skeleton variant="rect" width={118} height={36} style={pill} />
            <Skeleton variant="rect" width={148} height={36} style={pill} />
          </div>
        ) : null}
        <div className={classes.catalogueCards}>
          {Array.from({ length: 4 }).map((_, index) => (
            <CatalogueCardSkeleton classes={classes} detailed={showAccess} key={index} />
          ))}
        </div>
        <div className={classes.paginationRow}>
          {showAccess ? <Skeleton variant="rect" width={110} height={40} style={field} /> : null}
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rect" width={32} height={32} style={{ borderRadius: 8 }} />
          ))}
        </div>
      </div>
    </div>
  </>
);

CourseCatalogueLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  showAccess: PropTypes.bool,
};

const CourseCatalogueSkeleton = ({ classes }) => <CourseCatalogueLayout classes={classes} />;
CourseCatalogueSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const LearningHubSkeleton = ({ classes }) => <CourseCatalogueLayout classes={classes} showAccess />;
LearningHubSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const CompactTableSkeleton = ({ classes, rows = 7 }) => (
  <div className={`${classes.panel} ${classes.table}`}>
    <div className={classes.compactTableHeader}>
      {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} width="68%" />)}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div className={classes.compactTableRow} key={rowIndex}>
        {Array.from({ length: 6 }).map((_, columnIndex) => (
          <Skeleton key={columnIndex} width={columnIndex === 0 ? '88%' : '72%'} />
        ))}
      </div>
    ))}
  </div>
);

CompactTableSkeleton.propTypes = {
  classes: PropTypes.object.isRequired,
  rows: PropTypes.number,
};

const ReportFilterSkeleton = ({ classes }) => (
  <div className={classes.filterPanel}>
    <div className={classes.filterHeader}>
      <Skeleton width={180} height={30} />
      <div style={{ display: 'flex', gap: 8 }}>
        <Skeleton variant="circle" width={34} height={34} />
        <Skeleton variant="circle" width={34} height={34} />
      </div>
    </div>
    <div className={classes.filterGrid}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} variant="rect" height={42} style={{ borderRadius: 7 }} />
      ))}
    </div>
  </div>
);

ReportFilterSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const ReportTableLayout = ({ classes, tabCount, showReportTitle = false, testId }) => (
  <>
    <PageTitle classes={classes} />
    <div className={`${classes.panel} ${classes.reportPanel}`} data-testid={testId}>
      <div className={classes.reportTabs}>
        {Array.from({ length: tabCount }).map((_, index) => (
          <Skeleton key={index} width={index === 0 ? 180 : 128} height={28} />
        ))}
      </div>
      <div className={classes.reportBody}>
        {showReportTitle ? <Skeleton width={220} height={34} style={{ marginBottom: 12 }} /> : null}
        <ReportFilterSkeleton classes={classes} />
        <CompactTableSkeleton classes={classes} />
        <div className={classes.pagination}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rect" width={30} height={30} style={{ borderRadius: 6 }} />
          ))}
        </div>
      </div>
    </div>
  </>
);

ReportTableLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  tabCount: PropTypes.number.isRequired,
  showReportTitle: PropTypes.bool,
  testId: PropTypes.string.isRequired,
};

const ReportTableSkeleton = ({ classes }) => (
  <ReportTableLayout classes={classes} tabCount={3} showReportTitle testId="report-table-skeleton" />
);
ReportTableSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const MaturityReportSkeleton = ({ classes }) => (
  <ReportTableLayout classes={classes} tabCount={2} testId="maturity-report-skeleton" />
);
MaturityReportSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const ReportOverviewSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <div className={classes.reportCards} data-testid="report-overview-skeleton">
      {Array.from({ length: 3 }).map((_, index) => (
        <div className={`${classes.panel} ${classes.reportCard}`} key={index}>
          <div className={classes.reportCardHeader}>
            <Skeleton variant="rect" width={48} height={48} style={{ borderRadius: 12 }} />
            <div>
              <Skeleton width="44%" />
              <Skeleton width="76%" height={28} />
            </div>
          </div>
          <div className={classes.reportCardActions}>
            <Skeleton variant="rect" width={88} height={32} style={{ borderRadius: 8 }} />
          </div>
        </div>
      ))}
    </div>
    <div className={classes.reportKpis}>
      {Array.from({ length: 4 }).map((_, index) => (
        <div className={`${classes.panel} ${classes.reportKpi}`} key={index}>
          <Skeleton width="58%" />
          <Skeleton width="36%" height={48} />
          <Skeleton width="76%" />
        </div>
      ))}
    </div>
    <div className={classes.reportCharts}>
      <div className={`${classes.panel} ${classes.reportChart}`}>
        <Skeleton width="34%" height={32} />
        <Skeleton variant="rect" height={238} style={{ borderRadius: 10, marginTop: 18 }} />
      </div>
      <div className={`${classes.panel} ${classes.reportChart}`}>
        <Skeleton width="62%" height={32} />
        <Skeleton variant="circle" width={218} height={218} style={{ margin: '18px auto 0' }} />
      </div>
    </div>
  </>
);

ReportOverviewSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const CalendarSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <div className={`${classes.panel} ${classes.calendarMain}`}>
          <div className={classes.calendarToolbar}>
            <Skeleton width="28%" height={36} />
            <Skeleton variant="rect" width={176} height={38} style={{ borderRadius: 6 }} />
          </div>
          <div className={classes.calendarGrid}>
            {Array.from({ length: 35 }).map((_, index) => (
              <div className={classes.calendarCell} key={index}>
                <Skeleton width="30%" />
                {index % 4 === 0 ? <Skeleton height={28} /> : null}
              </div>
            ))}
          </div>
        </div>
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.panel} style={{ minHeight: 260 }}>
          <Skeleton width="45%" height={34} />
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} />)}
        </div>
        <div className={classes.panel} style={{ minHeight: 220, marginTop: 16 }}>
          <Skeleton width="55%" height={34} />
          {Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} />)}
        </div>
      </Grid>
    </Grid>
  </>
);

CalendarSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const ContentSkeleton = ({ classes }) => (
  <>
    <PageTitle classes={classes} />
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <div className={`${classes.panel} ${classes.contentPanel}`}>
          <Skeleton width="36%" height={34} />
          {Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} />)}
        </div>
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={`${classes.panel} ${classes.contentPanel}`}>
          <Skeleton width="52%" height={34} />
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} />)}
        </div>
      </Grid>
    </Grid>
  </>
);

ContentSkeleton.propTypes = { classes: PropTypes.object.isRequired };

const layouts = {
  dashboard: DashboardSkeleton,
  table: TableSkeleton,
  form: FormSkeleton,
  cards: CardsSkeleton,
  calendar: CalendarSkeleton,
  'course-list': CourseListSkeleton,
  'course-catalogue': CourseCatalogueSkeleton,
  'learning-hub': LearningHubSkeleton,
  'report-overview': ReportOverviewSkeleton,
  'report-table': ReportTableSkeleton,
  'maturity-report': MaturityReportSkeleton,
  content: ContentSkeleton,
};

const RouteSkeleton = ({ fullPage = false, variant = 'content', showProgress = true }) => {
  const classes = useStyles();
  const SkeletonLayout = layouts[variant] || ContentSkeleton;

  return (
    <div
      className={`${classes.skeleton}${fullPage ? ` ${classes.fullPage}` : ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`Loading ${variant} page`}
      data-testid="route-skeleton"
      data-skeleton-variant={variant}
    >
      {showProgress ? <LinearProgress
        className={fullPage ? classes.fullPageProgress : classes.progress}
        aria-label="Loading page progress"
      /> : null}
      <SkeletonLayout classes={classes} />
    </div>
  );
};

RouteSkeleton.propTypes = {
  showProgress: PropTypes.bool,
  fullPage: PropTypes.bool,
  variant: PropTypes.oneOf(SKELETON_VARIANTS),
};

export default RouteSkeleton;
